import { createEvent, createStore, sample, combine } from 'effector'
import { aye, nay } from '~/shared/helpers'
import { appearanceFeature } from '~/features/appearance'
import { produce } from 'immer'
import { Empty } from './lib'

import { getRowsFx } from './request'
import { $currentRows, loadRows, resetRows } from './model'

const $identifier = appearanceFeature.$currentIdentifier

/*
 * Dynamic and lazy loading list of rows
 */

// first rows load request count
const INITIAL_LOAD_COUNT = 12

// next rows load requests count
const LAZY_LOAD_BY_COUNT = 12

export const loadMoreRows = createEvent<{ identifier: string }>()
export const loadMoreCurrentRows = createEvent()

// Identifire: loaded rows
const $loadedRowsCount = createStore<Record<string, number>>({}).reset(
  resetRows
)

export const $isRowsLoading = getRowsFx.pending

// rows loaded state // Identifire: loaded rows
export const $isRowsLoadedById = createStore<Record<string, boolean>>({})
  .on(getRowsFx.done, (state, { params, result }) => {
    return { ...state, [params.identifier]: aye(result.last) }
  })
  .reset(resetRows)

export const $isRowsLoaded = combine(
  $isRowsLoadedById,
  $identifier,
  (loadedStates, identifire) =>
    identifire ? aye(loadedStates[identifire]) : false
)

// need load more Rows on current page
sample({
  clock: loadMoreCurrentRows,
  source: $identifier,
  filter: Boolean,
  fn: (identifier) => ({ identifier }),
  target: loadMoreRows,
})

// initial rows load request
sample({
  clock: loadRows,
  target: loadMoreRows,
})

// first and next rows load request
// - on the next requests loads page N with LAZY_LOAD_BY_COUNT rows
// - on the first request loads page 0 with INITIAL_LOAD_COUNT rows
sample({
  clock: loadMoreRows,
  source: {
    loadedRowsCount: $loadedRowsCount,
    isRowsLoaded: $isRowsLoadedById,
    pending: getRowsFx.pending,
  },
  filter: ({ isRowsLoaded }, { identifier }) => nay(isRowsLoaded[identifier]),
  fn: ({ loadedRowsCount }, { identifier }) => {
    const count = loadedRowsCount[identifier] ?? 0

    return {
      page: Math.trunc(count / LAZY_LOAD_BY_COUNT),
      count: count === 0 ? INITIAL_LOAD_COUNT : LAZY_LOAD_BY_COUNT,
      identifier,
    }
  },
  target: getRowsFx,
})

// add empty content on rows load
sample({
  clock: getRowsFx.done,
  source: {
    currentRows: $currentRows,
    identifier: $identifier,
  },
  filter: ({ identifier }, { params }) => {
    return identifier === params.identifier
  },
  fn: ({ currentRows }, { result }) => {
    const updated = currentRows ? new Map(currentRows) : new Map()

    for (const row of result.content) {
      const exist = updated.get(row.id)

      if (exist) {
        // Update description only
        updated.set(row.id, { ...exist, desc: row })
      } else {
        // Insert empty data for new rows
        updated.set(row.id, { data: Empty(), desc: row })
      }
    }

    return updated
  },
  target: $currentRows,
})

sample({
  clock: getRowsFx.done,
  source: $loadedRowsCount,
  fn: (loadedRowsCount, { params }) =>
    produce(loadedRowsCount, (draft) => {
      const { page, count, identifier } = params
      draft[identifier] = page * count + count
    }),
  target: $loadedRowsCount,
})
